import { funnelStep } from './funnel';
import { routePattern } from './routePatterns';

/**
 * `error_shown`, for every error the builder puts on screen (the owner's journey log, 2026-10-04).
 *
 * Every screen says its errors the same way — an element with role="alert" (36 places today) — so
 * they are read off the page rather than reported from each: a new screen's errors count without
 * anybody remembering to. What is sent is WHERE (the route, as a name: `new_plan`) and a CODE — the
 * alert's `data-error-code` when it has one, else `shown` — never the words, which can carry an
 * idea, an app name or an email.
 *
 * Each alert element is counted once; one that is emptied and filled again (a second failure on
 * the same field) counts again. Returns the stop.
 */
export function whereFor(pathname: string): string {
  const name = routePattern(pathname)
    .replace(/:[a-z]+/gi, '')
    .replace(/[^a-z0-9]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();
  return (name || 'home').slice(0, 40).replace(/^[^a-z]+/, '') || 'home';
}

export function watchErrorsShown(root: Node = document.body): () => void {
  if (typeof MutationObserver === 'undefined' || !root) return () => undefined;
  const said = new WeakMap<Element, string>();
  const check = (el: Element): void => {
    if (el.getAttribute('role') !== 'alert') return;
    const text = (el.textContent ?? '').trim();
    if (!text) {
      said.delete(el);
      return;
    }
    if (said.get(el) === text) return;
    said.set(el, text);
    const code = el.getAttribute('data-error-code') ?? 'shown';
    funnelStep('error_shown', { where: whereFor(window.location.pathname), code }, { pixel: false });
  };
  // What changed, and the alert it sits in: added nodes, and text or children changed inside one.
  const scan = (node: Node, deep: boolean): void => {
    const el = node instanceof Element ? node : node.parentElement;
    if (!el) return;
    const host = el.closest('[role="alert"]');
    if (host) check(host);
    if (deep && node instanceof Element) node.querySelectorAll('[role="alert"]').forEach(check);
  };
  const observer = new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => scan(n, true));
      scan(r.target, false);
    }
  });
  observer.observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['role'] });
  return () => observer.disconnect();
}
